<template>
  <v-container fluid class="pa-4">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <div>
          <div class="text-h4 font-weight-bold">
            <v-icon size="large" class="mr-2">mdi-package-variant</v-icon>
            {{ editMode ? 'Editar' : 'Crear' }} Servicio Completo
          </div>
          <div class="text-subtitle-2 text-grey mt-1">
            Configure el servicio con todas sus secciones e items
          </div>
        </div>
        <v-btn
          color="grey"
          variant="outlined"
          prepend-icon="mdi-arrow-left"
          @click="$emit('back')"
        >
          Volver
        </v-btn>
      </v-card-title>

      <v-divider class="mb-6" />

      <v-card-text>
        <v-form ref="form" v-model="valid">
          <!-- ========================================= -->
          <!-- NIVEL 1: SERVICIO -->
          <!-- ========================================= -->
          <v-card variant="outlined" class="mb-6">
            <v-card-title class="bg-primary text-white">
              <v-icon start>mdi-briefcase</v-icon>
              Nivel 1: Información del Servicio
            </v-card-title>
            <v-card-text class="pt-4">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="servicio.NombreServicio"
                    label="Nombre del Servicio *"
                    :rules="[rules.required]"
                    variant="outlined"
                    prepend-inner-icon="mdi-tag"
                    hint="Ej: Ceremonia, Recepción, Decoración"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="servicio.Descripcion"
                    label="Descripción del Servicio"
                    variant="outlined"
                    rows="3"
                    prepend-inner-icon="mdi-text"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- ========================================= -->
          <!-- NIVEL 2 Y 3: SECCIONES E ITEMS -->
          <!-- ========================================= -->
          <v-card variant="outlined" class="mb-4">
            <v-card-title class="bg-secondary text-white d-flex justify-space-between">
              <span>
                <v-icon start>mdi-view-grid</v-icon>
                Nivel 2 y 3: Secciones e Items
              </span>
              <v-btn
                color="white"
                variant="text"
                prepend-icon="mdi-plus"
                @click="agregarSeccion"
              >
                Agregar Sección
              </v-btn>
            </v-card-title>

            <v-card-text class="pt-4">
              <!-- Lista de Secciones -->
              <v-expansion-panels v-if="secciones.length > 0" multiple>
                <v-expansion-panel
                  v-for="(seccion, secIndex) in secciones"
                  :key="secIndex"
                  elevation="2"
                  class="mb-3"
                >
                  <v-expansion-panel-title>
                    <div class="d-flex align-center justify-space-between w-100 pr-4">
                      <div class="d-flex align-center">
                        <v-icon class="mr-3" color="secondary">mdi-folder</v-icon>
                        <div>
                          <div class="font-weight-bold">
                            {{ seccion.NombreSeccion || 'Sección sin nombre' }}
                          </div>
                          <div class="text-caption text-grey">
                            {{ seccion.items.length }} item(s) configurado(s)
                          </div>
                        </div>
                      </div>
                      <v-btn
                        icon="mdi-delete"
                        size="small"
                        color="error"
                        variant="text"
                        @click.stop="eliminarSeccion(secIndex)"
                      />
                    </div>
                  </v-expansion-panel-title>

                  <v-expansion-panel-text>
                    <!-- Formulario de Sección -->
                    <v-card variant="flat" class="mb-4 pa-4 bg-grey-lighten-4">
                      <v-row>
                        <v-col cols="12" md="12">
                          <v-text-field
                            v-model="seccion.NombreSeccion"
                            label="Nombre de la Sección *"
                            :rules="[rules.required]"
                            variant="outlined"
                            density="comfortable"
                            prepend-inner-icon="mdi-tag-text"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-textarea
                            v-model="seccion.Descripcion"
                            label="Descripción"
                            variant="outlined"
                            rows="2"
                            density="comfortable"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                    </v-card>

                    <!-- Items de la Sección -->
                    <div class="mt-4">
                      <div class="d-flex justify-space-between align-center mb-3">
                        <div class="text-h6">
                          <v-icon class="mr-2">mdi-shape</v-icon>
                          Items de la Sección
                        </div>
                        <v-btn
                          color="primary"
                          size="small"
                          prepend-icon="mdi-plus"
                          @click="agregarItem(secIndex)"
                        >
                          Agregar Item
                        </v-btn>
                      </div>

                      <!-- Lista de Items -->
                      <v-list v-if="seccion.items.length > 0" class="pa-0">
                        <v-list-item
                          v-for="(item, itemIndex) in seccion.items"
                          :key="itemIndex"
                          class="mb-3 border rounded"
                          elevation="1"
                        >
                          <template #prepend>
                            <v-avatar
                              v-if="item.Imagenes.length > 0"
                              :image="item.Imagenes[0]"
                              size="60"
                              rounded
                            />
                            <v-avatar v-else color="grey-lighten-2" size="60" rounded>
                              <v-icon>mdi-image-off</v-icon>
                            </v-avatar>
                          </template>

                          <v-list-item-title class="font-weight-bold mb-1">
                            {{ item.NombreItem || 'Item sin nombre' }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            <v-chip
                              size="small"
                              color="success"
                              prepend-icon="mdi-currency-usd"
                              class="mr-2"
                            >
                              {{ formatCurrency(item.PrecioUnitario) }}
                            </v-chip>
                            <v-chip v-if="item.Imagenes.length > 0" size="small">
                              {{ item.Imagenes.length }} imagen(es)
                            </v-chip>
                          </v-list-item-subtitle>

                          <template #append>
                            <v-btn
                              icon="mdi-pencil"
                              size="small"
                              variant="text"
                              color="primary"
                              @click="editarItem(secIndex, itemIndex)"
                            />
                            <v-btn
                              icon="mdi-delete"
                              size="small"
                              variant="text"
                              color="error"
                              @click="eliminarItem(secIndex, itemIndex)"
                            />
                          </template>
                        </v-list-item>
                      </v-list>

                      <v-alert v-else type="info" variant="tonal" density="compact">
                        No hay items en esta sección. Haga clic en "Agregar Item" para comenzar.
                      </v-alert>
                    </div>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-alert v-else type="info" variant="tonal" prominent>
                <div class="text-h6 mb-2">No hay secciones</div>
                Agregue al menos una sección para comenzar a configurar el servicio.
              </v-alert>
            </v-card-text>
          </v-card>

          <!-- Botones de Acción -->
          <v-card variant="outlined">
            <v-card-text>
              <v-row>
                <v-col cols="12" class="d-flex justify-end gap-2">
                  <v-btn
                    color="grey"
                    variant="outlined"
                    size="large"
                    @click="$emit('back')"
                  >
                    Cancelar
                  </v-btn>
                  <v-btn
                    color="primary"
                    variant="elevated"
                    size="large"
                    :loading="saving"
                    :disabled="!valid || secciones.length === 0"
                    prepend-icon="mdi-content-save"
                    @click="guardarTodo"
                  >
                    Guardar Todo
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Dialog para editar Item -->
    <v-dialog v-model="dialogItem" max-width="800px" persistent scrollable>
      <v-card v-if="itemEditando">
        <v-card-title class="bg-primary text-white">
          <v-icon start>mdi-shape</v-icon>
          {{ itemEditando.index !== null ? 'Editar' : 'Nuevo' }} Item
        </v-card-title>

        <v-card-text style="max-height: 600px" class="pt-4">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="itemEditando.data.NombreItem"
                label="Nombre del Item *"
                :rules="[rules.required]"
                variant="outlined"
                prepend-inner-icon="mdi-tag"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="itemEditando.data.PrecioUnitario"
                label="Precio Unitario *"
                type="number"
                step="0.01"
                :rules="[rules.required, rules.positive]"
                variant="outlined"
                prefix="$"
                prepend-inner-icon="mdi-currency-usd"
              />
            </v-col>
            <!-- <v-col cols="12" md="6">
              <v-text-field
                v-model.number="itemEditando.data.Orden"
                label="Orden"
                type="number"
                variant="outlined"
                prepend-inner-icon="mdi-sort"
              />
            </v-col> -->
            <v-col cols="12">
              <v-textarea
                v-model="itemEditando.data.Descripcion"
                label="Descripción"
                variant="outlined"
                rows="3"
                prepend-inner-icon="mdi-text"
              />
            </v-col>

            <!-- Gestión de imágenes del item -->
            <!-- <v-col cols="12">
              <v-card variant="outlined">
                <v-card-title class="text-subtitle-1">
                  <v-icon start>mdi-image-multiple</v-icon>
                  Imágenes del Item
                </v-card-title>
                <v-card-text>
                  <v-text-field
                    v-model="itemEditando.nuevaImagen"
                    label="URL de la imagen"
                    variant="outlined"
                    density="compact"
                    append-inner-icon="mdi-plus"
                    @click:append-inner="agregarImagenItemEditando"
                    @keyup.enter="agregarImagenItemEditando"
                  />

                  <v-row v-if="itemEditando.data.Imagenes.length > 0" class="mt-2">
                    <v-col
                      v-for="(img, index) in itemEditando.data.Imagenes"
                      :key="index"
                      cols="6"
                      sm="4"
                      md="3"
                    >
                      <v-card>
                        <v-img :src="img" height="120" cover />
                        <v-card-actions>
                          <v-spacer />
                          <v-btn
                            icon="mdi-delete"
                            size="small"
                            color="error"
                            variant="text"
                            @click="eliminarImagenItemEditando(index)"
                          />
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>

                  <v-alert v-else type="info" variant="tonal" density="compact">
                    No hay imágenes agregadas
                  </v-alert>
                </v-card-text>
              </v-card>
            </v-col> -->
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" variant="text" @click="cancelarEditarItem">
            Cancelar
          </v-btn>
          <v-btn color="primary" variant="elevated" @click="guardarItem">
            Guardar Item
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarText }}
      <template #actions>
        <v-btn variant="text" @click="snackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

    <!-- Dialog de progreso -->
    <v-dialog v-model="dialogProgreso" persistent max-width="400">
      <v-card>
        <v-card-text class="pa-6">
          <div class="text-center">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
              class="mb-4"
            />
            <div class="text-h6">{{ progresoTexto }}</div>
            <div class="text-caption text-grey mt-2">Por favor espere...</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { useStore } from 'vuex'
const props = defineProps({
  servicioId: {
    type: Number,
    default: null
  }
})
const store = useStore()
const emit = defineEmits(['back', 'saved'])

// Estado
const valid = ref(false)
const saving = ref(false)
const editMode = ref(false)
const dialogItem = ref(false)
const dialogProgreso = ref(false)
const progresoTexto = ref('')
const form = ref(null)

// Datos
const tiposEvento = ref([])

// Servicio (Nivel 1)
const servicio = ref({
  ServicioID: null,
  NombreServicio: '',
  Descripcion: '',
  TipoEventoID: null,
  Orden: 0
})

// Secciones (Nivel 2)
const secciones = ref([])

// Item en edición
const itemEditando = ref(null)

// Snackbar
const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

// Reglas de validación
const rules = {
  required: (value) => !!value || 'Campo requerido',
  positive: (value) => value > 0 || 'Debe ser mayor a 0'
}

// Métodos
const fetchTiposEvento = async () => {
  await store.dispatch('Cotizacion/getTiposEvento')
  tiposEvento.value = store.state.Cotizacion.TiposEvento.data.data
}

const agregarSeccion = () => {
  secciones.value.push({
    SeccionID: null,
    NombreSeccion: '',
    Descripcion: '',
    Orden: secciones.value.length,
    Imagenes: [],
    nuevaImagen: '',
    items: []
  })
}

const eliminarSeccion = (index) => {
  if (confirm('¿Está seguro de eliminar esta sección y todos sus items?')) {
    secciones.value.splice(index, 1)
  }
}

const agregarImagenSeccion = (secIndex) => {
  const url = secciones.value[secIndex].nuevaImagen.trim()
  if (url) {
    secciones.value[secIndex].Imagenes.push(url)
    secciones.value[secIndex].nuevaImagen = ''
  }
}

const eliminarImagenSeccion = (secIndex, imgIndex) => {
  secciones.value[secIndex].Imagenes.splice(imgIndex, 1)
}

const agregarItem = (secIndex) => {
  itemEditando.value = {
    seccionIndex: secIndex,
    index: null,
    nuevaImagen: '',
    data: {
      ItemID: null,
      NombreItem: '',
      PrecioUnitario: 0,
      Descripcion: '',
      Orden: secciones.value[secIndex].items.length,
      Imagenes: []
    }
  }
  dialogItem.value = true
}

const editarItem = (secIndex, itemIndex) => {
  itemEditando.value = {
    seccionIndex: secIndex,
    index: itemIndex,
    nuevaImagen: '',
    data: { ...secciones.value[secIndex].items[itemIndex] }
  }
  dialogItem.value = true
}

const eliminarItem = (secIndex, itemIndex) => {
  if (confirm('¿Está seguro de eliminar este item?')) {
    secciones.value[secIndex].items.splice(itemIndex, 1)
  }
}

const agregarImagenItemEditando = () => {
  const url = itemEditando.value.nuevaImagen.trim()
  if (url) {
    itemEditando.value.data.Imagenes.push(url)
    itemEditando.value.nuevaImagen = ''
  }
}

const eliminarImagenItemEditando = (index) => {
  itemEditando.value.data.Imagenes.splice(index, 1)
}

const guardarItem = () => {
  if (!itemEditando.value.data.NombreItem || itemEditando.value.data.PrecioUnitario <= 0) {
    showSnackbar('Complete todos los campos requeridos del item', 'error')
    return
  }

  const { seccionIndex, index, data } = itemEditando.value

  if (index !== null) {
    // Editar item existente
    secciones.value[seccionIndex].items[index] = { ...data }
  } else {
    // Agregar nuevo item
    secciones.value[seccionIndex].items.push({ ...data })
  }

  cancelarEditarItem()
  showSnackbar('Item guardado en memoria', 'success')
}

const cancelarEditarItem = () => {
  itemEditando.value = null
  dialogItem.value = false
}

const guardarTodo = async () => {
  if (!valid.value) {
    showSnackbar('Complete todos los campos requeridos', 'error')
    return
  }

  if (secciones.value.length === 0) {
    showSnackbar('Debe agregar al menos una sección', 'error')
    return
  }

  // Validar que cada sección tenga al menos un item
  const seccionesSinItems = secciones.value.filter((s) => s.items.length === 0)
  if (seccionesSinItems.length > 0) {
    if (
      !confirm(
        `Hay ${seccionesSinItems.length} sección(es) sin items. ¿Desea continuar de todas formas?`
      )
    ) {
      return
    }
  }

  saving.value = true
  dialogProgreso.value = true
  try {
    // Paso 1: Crear el servicio
    const payload = {
      NombreServicio : servicio.value.NombreServicio,
      Descripcion: servicio.value.Descripcion || '',
      Secciones:  secciones.value

    }
    progresoTexto.value = 'Creando servicio...';
  await store.dispatch('Cotizacion/crearServicioCompletoTransaccional', payload)
      const result = store.state.Cotizacion.result
    
    if (result.success) {
      progresoTexto.value = `Servicios, Secciones e Items creados`
    }

    // Limpiar formulario y volver
    setTimeout(() => {
      emit('saved')
      emit('back')
      dialogProgreso.value = false
    }, 1000)
  } catch (error) {
    dialogProgreso.value = false
    // const errorMsg = error.response?.data?.message || 'Error al guardar el servicio completo'
    // showSnackbar(errorMsg, 'error')
    console.error(error)
  } finally {
    saving.value = false
  }
}

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(value)
}

const truncateUrl = (url) => {
  return url.length > 40 ? url.substring(0, 40) + '...' : url
}

const showSnackbar = (text, color = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  snackbar.value = true
}

// Lifecycle
onMounted(() => {
  fetchTiposEvento()
  
  // Si hay un servicioId, estamos en modo edición
  if (props.servicioId) {
    editMode.value = true
    // TODO: Cargar datos del servicio existente
  }
})
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>